<template>
  <v-container class="workflows-shell ma-0 pa-0" fluid>
    <div class="workflows-layout">
      <aside class="workflow-sidebar">
        <div class="d-flex align-center justify-space-between ml-1 workflow-sidebar-header">
          <Button icon="mdi-note-plus-outline" :click="addFactory" size="large" tooltip="New Factory" variant="plain"
            density="compact" color="primary" />
          <v-spacer></v-spacer>
          <v-tooltip text="Factories is currently in beta and may have bugs." location="top">
            <template v-slot:activator="{ props }">
              <v-chip v-bind="props" size="small" density="compact" color="secondary" class="mx-auto">
                BETA
              </v-chip>
            </template>
          </v-tooltip>
          <v-tooltip text="Refresh Factories" location="top">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" color="primary" variant="text" density="compact" icon :loading="refreshingFactories" @click="refreshFactoriesView(true)">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
        <v-list class="mt-2 workflow-history-list" bg-color="transparent" density="compact">
          <v-list-item
            v-for="factory in factoryList"
            :key="factory.id"
            :active="selectedFactoryId === factory.id"
            active-class="workflow-history-item--active"
            class="workflow-history-item"
            lines="one"
            @click="selectedFactoryId = factory.id"
          >
            <template #title>
              <v-list-item-title class="workflow-history-title" :title="factory.name">
                {{ factory.name }}
              </v-list-item-title>
            </template>

            <template #append>
              <div class="workflow-history-actions">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props: tooltip }">
                    <v-icon
                      v-bind="tooltip"
                      @click.stop="handleDeleteClick($event, factory.id)"
                      size="small"
                      color="primary"
                      class="workflow-delete-icon"
                    >
                      mdi-delete-forever-outline
                    </v-icon>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </div>
            </template>
          </v-list-item>
        </v-list>
      </aside>

      <section class="workflow-main">
        <v-window v-if="selectedFactoryId" v-model="selectedFactoryId" class="fill-height">
          <v-window-item v-for="factory in factoryList" :key="factory.id" :value="factory.id" class="workflow-scroll">
            <FactoryEditor :id="factory.id" :is-active="selectedFactoryId === factory.id" />
          </v-window-item>
        </v-window>
        <div v-else class="factory-empty-pane">
          <v-card class="factory-empty-card" variant="outlined">
            <v-card-text class="pa-8 text-center text-medium-emphasis">
              <v-icon icon="mdi-factory" size="48" color="grey" />
              <div class="text-h6 mt-4 mb-2">No factories yet</div>
              <div>Create an agent or workflow, then add a factory from the sidebar.</div>
            </v-card-text>
          </v-card>
        </div>
      </section>
    </div>

    <ConfirmationDialog
      v-model="showDeleteConfirmation"
      title="Confirm Factory Deletion"
      message="Are you sure you want to delete this factory?"
      item-type="factory"
      :item-name="factoryToDeleteName"
      @confirm="executeDeleteFactory"
      @cancel="showDeleteConfirmation = false"
    />
  </v-container>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapActions, mapGetters } from 'vuex'
import { nanoid } from 'nanoid'
import Button from '@/components/sub/Button.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import FactoryEditor from '@/components/factories/FactoryEditor.vue'
import type { ConfigRefreshTarget } from '@/store/global'
import type { FactoryConfig } from '@/types/factories'
import type { WorkflowConfig } from '@/types/workflows'
import type { AccessibleAgentItem } from '@/store/agents'

@Options({
  components: { Button, ConfirmationDialog, FactoryEditor },
  computed: { ...mapGetters(['factoryList', 'selectedTab', 'configRefreshToken', 'accessibleAgentItems', 'workflowList']) },
  methods: { ...mapActions(['fetchFactories', 'saveFactory', 'deleteFactory']) },
  watch: {
    factoryList: {
      handler(newList: FactoryConfig[]) {
        const selectedExists = newList.some((factory) => factory.id === this.selectedFactoryId)
        if ((newList.length > 0 && !selectedExists) || !this.selectedFactoryId) {
          this.selectedFactoryId = newList[0]?.id || null
        } else if (newList.length === 0) {
          this.selectedFactoryId = null
        }
      },
      immediate: true,
      deep: true
    },
    selectedTab(value: string) {
      if (value === 'factories') {
        void this.maybeRefreshFactories()
      }
    },
    factoriesRefreshToken() {
      void this.maybeRefreshFactories()
    }
  }
})
export default class Factories extends Vue {
  factoryList!: FactoryConfig[]
  selectedTab!: string
  configRefreshToken!: (target: ConfigRefreshTarget) => number
  accessibleAgentItems!: AccessibleAgentItem[]
  workflowList!: WorkflowConfig[]
  fetchFactories!: () => Promise<boolean>
  saveFactory!: (config: FactoryConfig) => Promise<boolean>
  deleteFactory!: (factoryId: string) => Promise<boolean>

  selectedFactoryId: string | null = null
  refreshingFactories = false
  lastHandledFactoriesRefreshToken = -1
  showDeleteConfirmation = false
  factoryIdToDelete: string | null = null

  get factoriesRefreshToken(): number {
    return this.configRefreshToken('factories')
  }

  async created() {
    if (this.selectedTab === 'factories') {
      await this.refreshFactoriesView(true)
    }
  }

  async refreshFactoriesView(force = false) {
    if (this.refreshingFactories) return
    this.refreshingFactories = true
    try {
      await this.fetchFactories()
      this.lastHandledFactoriesRefreshToken = this.factoriesRefreshToken
    } finally {
      this.refreshingFactories = false
    }
  }

  async maybeRefreshFactories(force = false) {
    if (this.selectedTab !== 'factories' && !force) return
    const needsRefresh = force || this.lastHandledFactoriesRefreshToken !== this.factoriesRefreshToken
    if (needsRefresh) {
      await this.refreshFactoriesView(force)
    }
  }

  async addFactory() {
    const firstAgent = this.accessibleAgentItems?.[0]
    const firstWorkflow = this.workflowList?.[0]

    if (!firstAgent && !firstWorkflow) {
      this.$store.dispatch(
        'updateSnackbar',
        {
          text: 'Create at least one agent or workflow before creating a factory.',
          color: 'warning'
        },
        { root: true }
      )
      return
    }

    const initialStep = firstAgent
      ? {
          stepId: nanoid(),
          index: 0,
          name: 'Step 1',
          kind: 'agent' as const,
          limitStepInvocations: false,
          agentRef: {
            agentRef: firstAgent.ref,
            ...(firstAgent.agentId ? { agentId: firstAgent.agentId } : {})
          },
          maxStepInvocations: 1,
          transition: { kind: 'stop' as const }
        }
      : {
          stepId: nanoid(),
          index: 0,
          name: 'Step 1',
          kind: 'workflow' as const,
          limitStepInvocations: false,
          workflowRef: { workflowConfigId: firstWorkflow.id, maxRepairAttempts: 0 },
          maxStepInvocations: 1,
          transition: { kind: 'stop' as const }
        }

    const newFactory: FactoryConfig = {
      id: nanoid(),
      name: 'New Factory',
      description: '',
      continuous: false,
      limitTotalInvocations: false,
      maxTotalInvocations: 10,
      steps: [initialStep],
      updatedAt: Date.now()
    }
    const success = await this.saveFactory(newFactory)
    if (success) {
      this.lastHandledFactoriesRefreshToken = this.factoriesRefreshToken
      this.selectedFactoryId = newFactory.id
    }
  }

  get factoryToDeleteName(): string {
    return this.factoryList.find((factory) => factory.id === this.factoryIdToDelete)?.name || ''
  }

  handleDeleteClick(event: Event, factoryId: string) {
    event.stopPropagation()
    this.factoryIdToDelete = factoryId
    this.showDeleteConfirmation = true
  }

  async executeDeleteFactory() {
    if (!this.factoryIdToDelete) {
      this.showDeleteConfirmation = false
      return
    }
    const deletingSelected = this.selectedFactoryId === this.factoryIdToDelete
    const deletedId = this.factoryIdToDelete
    const success = await this.deleteFactory(this.factoryIdToDelete)
    if (success) {
      this.lastHandledFactoriesRefreshToken = this.factoriesRefreshToken
    }
    if (success && deletingSelected) {
      const remaining = this.factoryList.filter((factory) => factory.id !== deletedId)
      this.selectedFactoryId = remaining[0]?.id || null
    }
    this.factoryIdToDelete = null
    this.showDeleteConfirmation = false
  }
}
</script>

<style scoped lang="scss">
.workflows-shell {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.workflows-layout {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 0;
}

.workflow-sidebar {
  width: 240px;
  min-width: 240px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 4px 0 4px 0;
  background: rgb(var(--v-theme-background));
}

.workflow-sidebar-header {
  flex: 0 0 auto;
  margin-left: 8px !important;
  min-height: 36px;
}

.workflow-main {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.factory-empty-pane {
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 16px 8px 16px 0;
}

.factory-empty-card {
  width: min(680px, 100%);
}

.workflow-scroll {
  height: 100%;
  overflow-y: auto;
}

.workflow-history-list {
  overflow-y: auto;
}

.workflow-history-item--active {
  background: rgba(var(--v-theme-primary), 0.12);
}
</style>
